import { Injectable, Injector, inject } from '@angular/core';
import { catchError, filter, of, take, timeout } from 'rxjs';
import { CameraService } from './camera.service';
import { VehicleMonitorMarkerService } from './vehicle-monitor-markers.service';
import { VehicleMonitorService, VehicleMonitorVehicle } from './vehicle-monitor.service';

@Injectable({ providedIn: 'root' })
export class VehicleAlertFocusService {
  private readonly injector = inject(Injector);
  private readonly vehicleMonitor = inject(VehicleMonitorService);
  private readonly vehicleMarkers = inject(VehicleMonitorMarkerService);

  isVehicleAlert(item: any): boolean {
    item = this.unwrap(item);
    if (!item) {
      return false;
    }
    const title = this.alertTitle(item);
    if (/unauthori[sz]ed\s+vehicle/i.test(title)) {
      return true;
    }
    const categoryId = Number(
      item.organizationIncidentCategoryId
      ?? item.OrganizationIncidentCategoryId
      ?? item.OrganizationIncidentCategory?.id
      ?? item.incidentCategory?.id
    );
    return categoryId === 1304 && !!(this.trackerIdFromAlert(item) || this.vehicleNoFromAlert(item));
  }

  leaveVehicleViewForStandardAlert(): void {
    if (this.vehicleMonitor.isVehicleViewOn()) {
      this.vehicleMonitor.setVehicleViewOn(false);
    }
    this.injector.get(CameraService).clearLookAt();
  }

  focusFromAlert(item: any): boolean {
    item = this.unwrap(item);
    if (!this.isVehicleAlert(item)) {
      return false;
    }
    const trackerId = this.trackerIdFromAlert(item);
    const vehicleNo = this.vehicleNoFromAlert(item);
    const fallback = this.alertPoint(item);
    if (!this.vehicleMonitor.isVehicleViewOn()) {
      this.vehicleMonitor.setVehicleViewOn(true);
    }
    const live = this.findLiveVehicle(trackerId, vehicleNo);
    if (live) {
      this.applyFocus(live.trackerId, live.latitude, live.longitude);
      return true;
    }
    this.vehicleMonitor.latestSnapshot$.pipe(
      filter((snapshot) => !!this.findInSnapshot(snapshot, trackerId, vehicleNo) || (!!snapshot && !trackerId && !vehicleNo)),
      take(1),
      timeout({ first: 4000 }),
      catchError(() => of(this.vehicleMonitor.latestSnapshot()))
    ).subscribe((snapshot) => {
      const vehicle = this.findInSnapshot(snapshot, trackerId, vehicleNo);
      this.applyFocus(
        vehicle?.trackerId || trackerId,
        vehicle?.latitude ?? fallback?.lat,
        vehicle?.longitude ?? fallback?.lon
      );
    });
    return true;
  }

  private applyFocus(trackerId: string | null | undefined, lat?: number, lon?: number): void {
    if (typeof lat === 'number' && typeof lon === 'number' && Number.isFinite(lat) && Number.isFinite(lon)) {
      this.injector.get(CameraService).flyToVehicleFocus(lat, lon);
    }
    if (trackerId) {
      this.vehicleMarkers.focusTracker(trackerId);
    }
  }

  private findLiveVehicle(trackerId: string | null, vehicleNo: string | null): VehicleMonitorVehicle | undefined {
    return this.findInSnapshot(this.vehicleMonitor.latestSnapshot(), trackerId, vehicleNo);
  }

  private findInSnapshot(
    snapshot: { vehicles?: VehicleMonitorVehicle[] } | null | undefined,
    trackerId: string | null,
    vehicleNo: string | null
  ): VehicleMonitorVehicle | undefined {
    const vehicles = snapshot?.vehicles || [];
    if (trackerId) {
      const byId = vehicles.find((vehicle) => String(vehicle.trackerId) === trackerId);
      if (byId) {
        return byId;
      }
    }
    if (vehicleNo) {
      const needle = vehicleNo.trim().toLowerCase();
      return vehicles.find((vehicle) => String(vehicle.vehicleNo || '').trim().toLowerCase() === needle);
    }
    return undefined;
  }

  private unwrap(item: any): any {
    return item?.object || item?.entity?.object || item?.alertEntity || item;
  }

  private alertTitle(item: any): string {
    return String(item?.name || item?.description || item?.title || item?.object?.name || '');
  }

  private alertFeatures(item: any): any[] {
    const raw = item?.AlertFeatures || item?.alertFeatures || item?.features || item?.object?.AlertFeatures || [];
    return Array.isArray(raw) ? raw : [];
  }

  private featureProperties(feature: any): Record<string, unknown> {
    let properties = feature?.properties;
    if (typeof properties === 'string') {
      try {
        properties = JSON.parse(properties);
      } catch {
        return {};
      }
    }
    return properties && typeof properties === 'object' ? properties as Record<string, unknown> : {};
  }

  trackerIdFromAlert(item: any): string | null {
    for (const feature of this.alertFeatures(item)) {
      const properties = this.featureProperties(feature);
      const trackerId = properties['trackerId'] ?? properties['tracker_id'];
      if (trackerId) {
        return String(trackerId);
      }
    }
    return null;
  }

  private vehicleNoFromAlert(item: any): string | null {
    for (const feature of this.alertFeatures(item)) {
      const properties = this.featureProperties(feature);
      const vehicleNo = properties['vehicleNo'] ?? properties['vehicle_no'];
      if (vehicleNo) {
        return String(vehicleNo);
      }
    }
    const title = this.alertTitle(item);
    const match = title.match(/vehicle(?:\s+no\.?)?\s+(.+?)\s+has entered/i);
    return match?.[1]?.trim() || null;
  }

  private alertPoint(item: any): { lat: number; lon: number } | null {
    for (const feature of this.alertFeatures(item)) {
      const coords = feature?.geometry?.coordinates;
      if (Array.isArray(coords) && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
        return { lon: coords[0], lat: coords[1] };
      }
    }
    return null;
  }
}
